import { useEffect, useState } from "react";
import { ONBOARDING_SPORTS } from "../../../constants/onboardingSports";
import s from "../clinic.module.css";
import { Button, Field, Input, Select } from "../../ui";

const MAX_NAME_LEN = 100;

function directionIcon(name) {
  const n = String(name || "").trim().toLowerCase();
  const sport = ONBOARDING_SPORTS.find((item) => item.name.toLowerCase() === n);
  return sport?.icon || "🏅";
}

export function emptyGroupDraft(
  directions = [],
  branches = [],
  defaults = {},
) {
  return {
    name: "",
    direction_id: defaults.directionId || directions[0]?.id || "",
    branch_id: defaults.branchId || branches[0]?.id || "",
    max_capacity: "",
  };
}

export default function GroupDraftFields({
  form,
  onChange,
  directions = [],
  branches = [],
  onAdd,
  adding,
  addDisabled,
}) {
  const [showExtra, setShowExtra] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);

  const name = form.name || "";
  const nameTrimmed = name.trim();
  const nameTooLong = name.length > MAX_NAME_LEN;
  const nameEmpty = !nameTrimmed;

  let nameError = null;
  if (nameTouched || nameTooLong) {
    if (nameEmpty) nameError = "Введите название группы";
    else if (nameTooLong) nameError = `Не более ${MAX_NAME_LEN} символов`;
  }

  const directionId =
    form.direction_id && directions.some((d) => d.id === form.direction_id)
      ? form.direction_id
      : directions[0]?.id || "";

  const branchId =
    form.branch_id && branches.some((b) => b.id === form.branch_id)
      ? form.branch_id
      : branches[0]?.id || "";

  useEffect(() => {
    if (!directions.length) return;
    const firstId = directions[0].id;
    onChange((prev) => {
      if (
        prev.direction_id &&
        directions.some((d) => d.id === prev.direction_id)
      ) {
        return prev;
      }
      return { ...prev, direction_id: firstId };
    });
  }, [directions, onChange]);

  useEffect(() => {
    if (!branches.length) return;
    const firstId = branches[0].id;
    onChange((prev) => {
      if (prev.branch_id && branches.some((b) => b.id === prev.branch_id)) {
        return prev;
      }
      return { ...prev, branch_id: firstId };
    });
  }, [branches, onChange]);

  const canAdd =
    Boolean(nameTrimmed) &&
    !nameTooLong &&
    Boolean(branchId) &&
    Boolean(directionId) &&
    !addDisabled;

  const useDirectionCards = directions.length > 0 && directions.length <= 3;
  const useVenueCards = branches.length > 0 && branches.length <= 2;

  const handleAddKeyDown = (e) => {
    if (e.key === "Enter" && canAdd) {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <div className={s.onboardingGroupDraft}>
      <Field
        label="Название группы"
        hint="Будет отображаться в расписании и журнале"
        error={nameError}
      >
        <Input
          value={name}
          onChange={(e) =>
            onChange({ ...form, name: e.target.value.slice(0, MAX_NAME_LEN) })
          }
          onBlur={() => setNameTouched(true)}
          onKeyDown={handleAddKeyDown}
          placeholder="Например: Младшая группа"
          className={s.onboardingGroupInput}
          error={Boolean(nameError)}
          maxLength={MAX_NAME_LEN}
          autoFocus
        />
      </Field>

      <Field
        label="Направление"
        hint={
          directions.length > 1
            ? "Выберите направление для этой группы"
            : directions.length === 1
              ? "В секции одно направление"
              : "Сначала добавьте направление на шаге 2"
        }
      >
        {directions.length === 0 ? (
          <p className={s.onboardingGroupEmptyVenue}>
            Нет направлений — вернитесь к шагу «Направление»
          </p>
        ) : useDirectionCards ? (
          <div
            className={s.onboardingGroupDirectionCards}
            role="radiogroup"
            aria-label="Направление"
          >
            {directions.map((dir) => {
              const checked = directionId === dir.id;
              return (
                <label
                  key={dir.id}
                  className={
                    checked
                      ? s.onboardingGroupDirectionCardActive
                      : s.onboardingGroupDirectionCard
                  }
                >
                  <input
                    type="radio"
                    name="group-direction-draft"
                    value={dir.id}
                    checked={checked}
                    onChange={() =>
                      onChange({ ...form, direction_id: dir.id })
                    }
                  />
                  <span
                    className={s.onboardingGroupDirectionCardIcon}
                    aria-hidden
                  >
                    {directionIcon(dir.name)}
                  </span>
                  <span>{dir.name}</span>
                </label>
              );
            })}
          </div>
        ) : (
          <Select
            value={directionId}
            onChange={(e) =>
              onChange({ ...form, direction_id: e.target.value })
            }
            className={s.onboardingGroupSelect}
          >
            <option value="" disabled>
              Выберите направление
            </option>
            {directions.map((dir) => (
              <option key={dir.id} value={dir.id}>
                {dir.name}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field
        label="Зал"
        hint="Зал по умолчанию для занятий этой группы"
      >
        {branches.length === 0 ? (
          <p className={s.onboardingGroupEmptyVenue}>
            Сначала добавьте зал на предыдущем шаге
          </p>
        ) : useVenueCards ? (
          <div
            className={s.onboardingGroupVenueCards}
            role="radiogroup"
            aria-label="Зал"
          >
            {branches.map((branch) => {
              const checked = branchId === branch.id;
              return (
                <label
                  key={branch.id}
                  className={
                    checked
                      ? s.onboardingGroupVenueCardActive
                      : s.onboardingGroupVenueCard
                  }
                >
                  <input
                    type="radio"
                    name="group-venue-draft"
                    value={branch.id}
                    checked={checked}
                    onChange={() =>
                      onChange({ ...form, branch_id: branch.id })
                    }
                  />
                  <span>{branch.name}</span>
                </label>
              );
            })}
          </div>
        ) : (
          <Select
            value={branchId}
            onChange={(e) =>
              onChange({ ...form, branch_id: e.target.value })
            }
            className={s.onboardingGroupSelect}
          >
            <option value="" disabled>
              Выберите зал
            </option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <div className={s.onboardingGroupExtra}>
        <button
          type="button"
          className={s.onboardingGroupExtraToggle}
          onClick={() => setShowExtra((v) => !v)}
          aria-expanded={showExtra}
        >
          {showExtra ? "Скрыть дополнительно" : "Дополнительно"}
        </button>
        {showExtra ? (
          <Field
            label="Максимум человек (необязательно)"
            className={s.onboardingGroupExtraField}
          >
            <Input
              type="number"
              min={1}
              inputMode="numeric"
              value={form.max_capacity}
              onChange={(e) =>
                onChange({ ...form, max_capacity: e.target.value })
              }
              placeholder="12"
              className={s.onboardingGroupInput}
            />
          </Field>
        ) : null}
      </div>

      <Button
        type="button"
        className={s.onboardingGroupAddBtn}
        disabled={!canAdd || adding}
        onClick={onAdd}
        block
      >
        {adding ? "Добавление…" : "+ Добавить группу"}
      </Button>
    </div>
  );
}
